import React, { useEffect, useState } from "react";
import axios from "axios";
import shared from "../styles/shared.module.css";
import styles from "../styles/AdminEmpresas.module.css";
import { Check, X, RotateCcw, Trash2 } from "lucide-react";

type Empresa = {
  id: number;
  nome: string;
  nif: string;
  email: string;
  telefone: string;
  rua: string;
  cidade: string;
  codigoPostal: string;
  pais: string;
  descricao: string;
  nib: string;
  numeroCertidaoPermanente: string;
  status: "PENDENTE" | "APROVADA" | "REJEITADA";
  motivoRejeicao?: string;
};

const AdminEmpresas = () => {
  const [empresasPendentes, setEmpresasPendentes] = useState<Empresa[]>([]);
  const [empresasAprovadas, setEmpresasAprovadas] = useState<Empresa[]>([]);
  const [abaAtiva, setAbaAtiva] = useState<"pendentes" | "aprovadas">("pendentes");

  const [expandedId, setExpandedId] = useState<number | null>(null);

  // 🔹 Controle do modal de rejeição
  const [showModal, setShowModal] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState("");
  const [empresaRejeitadaId, setEmpresaRejeitadaId] = useState<number | null>(null);

  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEmpresasPendentes = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/admins/empresas?status=PENDENTE`, { headers });
      setEmpresasPendentes(res.data);
    } catch (error) {
      console.error("Erro ao buscar empresas pendentes:", error);
    }
  };

  const fetchEmpresasAprovadas = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/admins/empresas?status=APROVADA`, { headers });
      setEmpresasAprovadas(res.data);
    } catch (error) {
      console.error("Erro ao buscar empresas aprovadas:", error);
    }
  };

  useEffect(() => {
    fetchEmpresasPendentes();
    fetchEmpresasAprovadas();
  }, []);

  const handleAprovar = async (id: number) => {
    await axios.put(`${BASE_URL}/admins/empresas/${id}/aprovar`, {}, { headers });
    fetchEmpresasPendentes();
    fetchEmpresasAprovadas();
  };

  // 🔹 Abre modal de rejeição
  const abrirModalRejeicao = (id: number) => {
    setEmpresaRejeitadaId(id);
    setMotivoRejeicao("");
    setShowModal(true);
  };

  // 🔹 Confirma rejeição
  const confirmarRejeicao = async () => {
    if (!empresaRejeitadaId || !motivoRejeicao.trim()) return;

    try {
      await axios.put(
        `${BASE_URL}/admins/empresas/${empresaRejeitadaId}/rejeitar`,
        { motivo: motivoRejeicao },
        { headers }
      );
      fetchEmpresasPendentes();
      fetchEmpresasAprovadas();
    } catch (error) {
      console.error("Erro ao rejeitar empresa:", error);
    } finally {
      setShowModal(false);
      setEmpresaRejeitadaId(null);
      setMotivoRejeicao("");
    }
  };

  const handleDesaprovar = async (id: number) => {
    await axios.put(`${BASE_URL}/admins/empresas/${id}/desaprovar`, {}, { headers });
    fetchEmpresasPendentes();
    fetchEmpresasAprovadas();
  };

  const handleDeletar = async (id: number) => {
    await axios.delete(`${BASE_URL}/admins/empresas/${id}`, { headers });
    fetchEmpresasPendentes();
    fetchEmpresasAprovadas();
  };

  const toggleExpand = (id: number) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const renderEmpresaCard = (empresa: Empresa) => {
    const isExpanded = expandedId === empresa.id;

    return (
      <div
        key={empresa.id}
        className={`${styles.cardEmpresa} ${isExpanded ? styles.expanded : styles.collapsed}`}
      >
        <div className={styles.summary} onClick={() => toggleExpand(empresa.id)}>
          <div className={styles.summaryInfo}>
            <strong className={styles.nomeEmpresa}>{empresa.nome}</strong>
            <span
              className={`${styles.status} ${empresa.status === "PENDENTE"
                  ? styles.statusPendente
                  : empresa.status === "APROVADA"
                    ? styles.statusAprovada
                    : styles.statusRejeitada
                }`}
            >
              {empresa.status === "PENDENTE" && "⏳ Pendente"}
              {empresa.status === "APROVADA" && "✅ Aprovada"}
              {empresa.status === "REJEITADA" && "❌ Rejeitada"}
            </span>
          </div>
          <button className={styles.toggleBtn}>
            {isExpanded ? "▲" : "▼"}
          </button>
        </div>

        <div
          className={`${styles.detailsWrapper} ${isExpanded ? styles.detailsOpen : styles.detailsClosed}`}
        >
          {isExpanded && (
            <div className={styles.details}>
              <p><strong>NIF:</strong> {empresa.nif}</p>
              <p><strong>Telefone:</strong> {empresa.telefone}</p>
              <p><strong>Endereço:</strong> {`${empresa.rua}, ${empresa.cidade}, ${empresa.codigoPostal}, ${empresa.pais}`}</p>
              <p><strong>Descrição:</strong> {empresa.descricao}</p>
              <p><strong>NIB:</strong> {empresa.nib}</p>
              <p><strong>Certidão Permanente:</strong> {empresa.numeroCertidaoPermanente}</p>
              {empresa.status === "REJEITADA" && empresa.motivoRejeicao && (
                <p><strong>Motivo da rejeição:</strong> {empresa.motivoRejeicao}</p>
              )}

              <div className={styles.actions}>
                {empresa.status === "PENDENTE" && (
                  <>
                    <button
                      className={styles.aprovarButton}
                      onClick={() => handleAprovar(empresa.id)}
                    >
                      <Check size={16} style={{ marginRight: "6px" }} />
                      Aprovar
                    </button>
                    <button
                      className={styles.rejeitarButton}
                      onClick={() => abrirModalRejeicao(empresa.id)}
                    >
                      <X size={16} style={{ marginRight: "6px" }} />
                      Rejeitar
                    </button>
                  </>
                )}
                {empresa.status === "APROVADA" && (
                  <button
                    className={styles.desaprovarButton}
                    onClick={() => handleDesaprovar(empresa.id)}
                  >
                    <RotateCcw size={16} style={{ marginRight: "6px" }} />
                    Revisar
                  </button>
                )}
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDeletar(empresa.id)}
                >
                  <Trash2 size={16} style={{ marginRight: "6px" }} />
                  Deletar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const lista = abaAtiva === "pendentes" ? empresasPendentes : empresasAprovadas;

  return (
    <div className={shared.container}>
      <h1>Gerenciar Empresas</h1>

      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${abaAtiva === "pendentes" ? styles.tabActive : ""}`}
          onClick={() => setAbaAtiva("pendentes")}
        >
          Pendentes
        </button>
        <button
          className={`${styles.tabButton} ${abaAtiva === "aprovadas" ? styles.tabActive : ""}`}
          onClick={() => setAbaAtiva("aprovadas")}
        >
          Aprovadas
        </button>
      </div>

      {lista.length === 0 ? (
        <p>Nenhuma empresa {abaAtiva}.</p>
      ) : (
        lista.map(empresa => renderEmpresaCard(empresa))
      )}

      {/* 🔹 Modal simples */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2>Rejeitar Empresa</h2>
            <textarea
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              placeholder="Digite o motivo da rejeição..."
              className={styles.textarea}
            />
            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className={styles.confirmButton} onClick={confirmarRejeicao}>
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmpresas;